
/**
 * Module dependencies.
 */

import { changePassword } from 'src/api/auth/change-password';
import { getNetworkErrorTranslatedKey } from 'src/core/utils/errors';
import { regexes } from 'src/core/utils/regexes';
import { useAuth } from 'src/context/auth/context';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslate } from 'src/core/utils/translator';
import Password from 'src/components/core/forms/fields/password';
import React, { ReactElement, useCallback, useRef } from 'react';
import Sidebar from 'src/components/my-revigres/core/sidebar';
import useAutoFocus from 'src/hooks/use-auto-focus';

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  isOpen: boolean,
  onClose: () => void
};

/**
 * Password length.
 */

const passwordLength = 6;

/**
 * `useChangePassword` hook.
 */

function useChangePassword() {
  const { translate } = useTranslate();
  const { showMessage } = useSnackbar();
  const { onUpdateToken, token } = useAuth();
  const { isLoading, mutateAsync } = useMutation(changePassword);
  const handleChangePassword = useCallback(formData => {
    return mutateAsync({ formData, token }, {
      onError: ({ data }) => {
        const translatedKey = getNetworkErrorTranslatedKey('changePassword', `${data?.code}`);

        showMessage(translate(`my-revigres:dashboard.user.${translatedKey}`), {
          appearance: 'error'
        });
      },
      onSuccess: ({ token }: { token: string | null }) => {
        onUpdateToken(token);
        showMessage(translate('my-revigres:dashboard.user.changePassword.successMessage'), {
          appearance: 'success'
        });
      }
    });
  }, [mutateAsync, onUpdateToken, showMessage, token, translate]);

  return {
    isSubmitting: isLoading,
    onChangePassword: handleChangePassword
  };
}

/**
 * `ChangePasswordSidebar` component.
 */

const ChangePasswordSidebar = (props: Props): ReactElement => {
  const { isOpen, onClose } = props;
  const { updateUser, user } = useAuth();
  const { translate } = useTranslate();
  const {
    errors,
    handleSubmit,
    register,
    reset
  } = useForm({ mode: 'onBlur' });

  const { isSubmitting, onChangePassword } = useChangePassword();
  const inputRef = useRef();
  const handleChangePassword = useCallback(data => {
    onChangePassword({ ...data, email: user?.authUser?.email })
      .then(() => {
        onClose();
        updateUser();
        reset();
      });
  }, [onChangePassword, onClose, reset, updateUser, user]);

  useAutoFocus({
    isActive: isOpen,
    ref: inputRef
  });

  return (
    <Sidebar
      isOpen={isOpen}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={handleSubmit(handleChangePassword)}
      title={translate('my-revigres:dashboard.menuOptions.password')}
    >
      <Password
        disabled={isSubmitting}
        error={errors?.oldPassword}
        label={translate('forms:oldPasswordLabel')}
        name={'oldPassword'}
        placeholder={translate('forms:oldPasswordPlaceholder')}
        ref={element => {
          // @ts-ignore
          inputRef.current = element;
          register<any>(element, {
            required: {
              message: translate('forms:requiredField'),
              value: true
            }
          });
        }}
      />

      <Password
        disabled={isSubmitting}
        error={errors?.password}
        helpText={translate('forms:newPasswordHelpText', { passwordLength })}
        label={translate('forms:newPasswordLabel')}
        name={'password'}
        placeholder={translate('forms:newPasswordPlaceholder')}
        ref={register<any>({
          pattern: {
            message: translate('forms:passwordSizeValidation', { passwordLength }),
            value: regexes.passwordRequirements.size
          },
          required: {
            message: translate('forms:requiredField'),
            value: true
          }
        })}
      />
    </Sidebar>
  );
};

/**
 * Export `ChangePasswordSidebar` component.
 */

export default ChangePasswordSidebar;
